// src/worker/customer-requests.js
//
// v132.78 - "customer asked, we don't have": each store's weekly list of items
// customers asked for that we don't stock. PURE (no I/O, no Worker-only
// imports), so the staff bundle and the tests can use it too.
//
// Matt, 28 Sep 2026: "I would like them to be able to add to it throughout the
// week and submit once, so you dont have to remember from Monday morning until
// you submit the final list Sunday night. Can also tally if you get multiple
// requests."

export const MAX_ITEMS_PER_WEEK = 60;     // a runaway guard, not a business rule
export const MAX_ASKS = 999;
export const ITEM_MAX = 120;
export const BRAND_MAX = 60;
export const NOTE_MAX = 240;

// The Monday of the trading week that contains dateStr (YYYY-MM-DD). The
// caller passes the BUSINESS day (3am to 3am), so a Sunday-night submission
// at 00:30 still belongs to the week it closes.
export function weekStartFor(dateStr) {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  const t = Date.UTC(y, m - 1, d);
  const dow = new Date(t).getUTCDay();            // 0 = Sunday
  const back = dow === 0 ? 6 : dow - 1;
  return new Date(t - back * 86400000).toISOString().slice(0, 10);
}
export function weekEndFor(weekStart) {
  const [y, m, d] = String(weekStart || '').split('-').map(Number);
  if (!y) return null;
  return new Date(Date.UTC(y, m - 1, d) + 6 * 86400000).toISOString().slice(0, 10);
}

function clean(s, max) {
  return String(s == null ? '' : s).replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max);
}

// "Bluey plush toys!" and "bluey  plush toy" are the same request. Lower case,
// punctuation out, spaces collapsed, a plain trailing plural "s" dropped from
// words longer than three letters. Deliberately simple: the store's own words
// always travel with the group, so a crude match loses nothing.
export function normaliseItem(text) {
  return clean(text, ITEM_MAX).toLowerCase()
    .replace(/[^a-z0-9؀-ۿ ]+/g, ' ')
    .split(' ').filter(Boolean)
    .map((w) => {
      if (w.length > 4 && /(sh|ch|x|ss|z)es$/.test(w)) return w.slice(0, -2);   // plushes, boxes, watches
      if (w.length > 3 && /[^s]s$/.test(w)) return w.slice(0, -1);             // toys, sets, kits
      return w;
    })
    .join(' ');
}

// Validate an add or an edit. Returns { ok, item, brand, note, key } or { ok:false, reason }.
export function validateRequestInput(body, opts) {
  const o = opts || {};
  const item = clean(body && body.item, ITEM_MAX);
  if (!item && !o.partial) return { ok: false, reason: 'Say what the customer asked for.' };
  if (item && item.length < 2) return { ok: false, reason: 'Please describe the item in a few words.' };
  const out = { ok: true };
  if (item) { out.item = item; out.key = normaliseItem(item); }
  if (body && body.brand !== undefined) out.brand = clean(body.brand, BRAND_MAX) || null;
  if (body && body.note !== undefined) out.note = clean(body.note, NOTE_MAX) || null;
  if (body && body.asks !== undefined) {
    const n = Number(body.asks);
    if (!Number.isInteger(n) || n < 1 || n > MAX_ASKS) return { ok: false, reason: 'The number of customers who asked must be a whole number from 1.' };
    out.asks = n;
  }
  return out;
}

// Group a week's lines across stores. rows: [{ store, country, item, item_key,
// brand, note, asks }]. Returns groups, most asked first:
//   { key, label, asks, stores: n, countries: [..], lines: [{ store, country, item, brand, note, asks }] }
// The label is the wording used for the most asks. Every line survives in
// `lines`, so nothing a store wrote is lost to the grouping.
export function groupRequests(rows) {
  const by = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r || !r.item) return;
    const key = r.item_key || normaliseItem(r.item);
    if (!key) return;
    if (!by.has(key)) by.set(key, []);
    by.get(key).push(r);
  });
  const groups = [...by.entries()].map(([key, lines]) => {
    const wording = {};
    lines.forEach((l) => { wording[l.item] = (wording[l.item] || 0) + (Number(l.asks) || 0); });
    const label = Object.keys(wording).sort((a, b) => wording[b] - wording[a] || a.localeCompare(b))[0];
    return {
      key, label,
      asks: lines.reduce((n, l) => n + (Number(l.asks) || 0), 0),
      stores: new Set(lines.map((l) => l.store)).size,
      countries: [...new Set(lines.map((l) => l.country).filter(Boolean))].sort(),
      lines: lines.map((l) => ({ store: l.store, country: l.country || null, item: l.item, brand: l.brand || null, note: l.note || null, asks: Number(l.asks) || 0 }))
        .sort((a, b) => b.asks - a.asks || a.store.localeCompare(b.store)),
    };
  });
  return groups.sort((a, b) => b.asks - a.asks || b.stores - a.stores || a.label.localeCompare(b.label));
}

// The raw data as CSV, one row per store line. RFC 4180 quoting; a leading
// = + - @ is prefixed with an apostrophe so a spreadsheet never runs it as a
// formula (store text is free text).
export function requestsCsv(rows, weekStart) {
  const q = (v) => {
    let s = String(v == null ? '' : v);
    if (/^[=+\-@]/.test(s)) s = "'" + s;
    return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const head = ['week_start', 'store', 'country', 'item', 'brand', 'customers_asked', 'note', 'submitted'];
  const lines = (Array.isArray(rows) ? rows : []).map((r) => [
    weekStart, r.store, r.country || '', r.item, r.brand || '', Number(r.asks) || 0, r.note || '', r.submitted ? 'yes' : 'no',
  ].map(q).join(','));
  return [head.join(',')].concat(lines).join('\r\n') + '\r\n';
}
